import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Device } from '@twilio/voice-sdk';
// eslint-disable-next-line camelcase
import { get_api, post_api, put_api } from '../../../redux/api_funcs';
import { set_notification_variable } from '../../../redux/action_creators/home_actions';
import { get_api_url } from '../../../utils/urls';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import MytNotification from '../../../myt_chat/notification';
import IncomingCallPill from './IncomingCallPill';
import { attachRemoteAudio, unlockTwilioAudio } from './twilio_audio';

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return get_api_url(endpoint);
};

// Server issues Voice tokens with a 1 h TTL (GenVoiceToken). Refresh well
// before expiry so `<Client>` routing never lapses.
const TOKEN_REFRESH_MS = 50 * 60 * 1000;

// PROD-9204. Every recovery trigger we had was an *event* — an SDK
// `unregistered`/`error`, or the browser's `online`. All of them can be missed:
//  - macOS sleep/wake usually never fires `online` (navigator.onLine never went
//    false), and both the SDK's 30 s presence heartbeat and our token timer are
//    setTimeouts that are frozen while the machine is asleep.
//  - the SDK registers its own `pagehide` -> destroy() handler, and destroy()
//    calls removeAllListeners(), so a frozen/bfcached tab loses the very
//    listeners self-heal hangs off.
// A cheap poll of `device.state` closes both holes: it is the only check that
// cannot itself be swallowed.
const DEVICE_HEALTH_POLL_MS = 30 * 1000;

// Device states that mean "cannot receive invites, and will not recover on its
// own". 'registering' is transient and must never trigger a rebuild.
const DEAD_DEVICE_STATES = new Set(['unregistered', 'destroyed']);

// How long we wait, after the `incoming_call` relay lands, for Twilio's SDK to
// actually deliver the invite (device.on('incoming')). The relay is what starts
// the ring sound + OS banner; the SDK invite is what renders the pill and gives
// the agent an Accept button. If the invite never arrives the agent is told a
// call is coming but has no way to answer it, and the caller rings on until the
// backend gives up and sends them to voicemail. Surface that instead of leaving
// it invisible.
//
// Calibration: in production the invite normally lands 2-3s after the relay, so
// the old 5s window left only ~2s of headroom and could false-positive on a
// Twilio latency spike (suspected near-threshold case on 2026-08-22, PROD-8664).
// 10s still leaves 15s of the 25s dial window for the warning to be actionable
// on a true positive.
const DEVICE_INVITE_WATCHDOG_MS = 10000;

// PROD-8664. A long-lived CMS tab can end up with a "ghost" Device: still
// registered from Twilio's point of view (it accepts the invite and returns
// ringing) but wired to a socket that never delivers the invite to our
// listeners, so the call never surfaces in the UI and the caller rings out.
// Re-creating the Device from scratch is the only reliable fix, and the
// natural moment to do it is when the agent toggles availability ON — the
// shift-start ritual they already perform.
//
// Floor between two re-registrations. Toggle flapping (or a burst of relays)
// would otherwise thrash Twilio's registration endpoint and leave a window
// with no registered Device at all — which is the very failure we're fixing.
const REREGISTER_MIN_INTERVAL_MS = 30 * 1000;

// PROD-8664, second half. The re-registration above only fires on events the
// *agent* or the *backend* generates (availability toggle, ghost detection). It
// does nothing about a registration that dies silently mid-shift: on 2026-08-22
// an agent toggled Available at 04:52, the Device was already dead by 06:35, and
// nothing healed it until a missed call tripped the invite watchdog nearly two
// hours later. The SDK does tell us — `unregistered` and connection-class
// `error` events fire — we simply weren't acting on them. Now we do.
//
// Backoff: 2s, 4s, 8s ... capped at 60s. Deliberately aggressive at the start
// (a Wi-Fi blip or a laptop wake is usually healed on the first retry) and slow
// at the tail (a Twilio-side outage shouldn't be hammered by every open CMS tab).
const SELF_HEAL_BASE_DELAY_MS = 2000;
const SELF_HEAL_MAX_DELAY_MS = 60 * 1000;
// ~8 attempts ≈ 2s+4s+8s+16s+32s+60s+60s+60s ≈ 4 min of trying. Past that the
// problem isn't transient, and an unbounded silent retry loop is worse than
// telling the agent plainly that they need to reload.
const SELF_HEAL_MAX_ATTEMPTS = 8;

// Shown once the backoff is exhausted, as a persistent banner rather than a
// toast: a toast that auto-dismisses is exactly how an agent ends up sitting on
// a dead Device with the availability toggle still showing "Available".
const SELF_HEAL_EXHAUSTED_MESSAGE =
  "Your calling device isn't registered and could not reconnect. You've been marked unavailable — reload the tab to start receiving calls again.";

// The invite watchdog fires when the relay landed but the SDK invite never did.
// A rebuild is already in flight by then (see the relay handler), so the agent
// is told what is happening rather than ordered to refresh.
const INVITE_MISSING_MESSAGE =
  'Incoming call did not reach your calling device. Reconnecting it now — the next call should come through.';

// Twilio error codes that mean "the transport/registration is broken", i.e. the
// Device can no longer receive invites and rebuilding it is the right response.
// Codes are from @twilio/voice-sdk's generated error table:
//   31003 GeneralErrors.ConnectionTimeoutError
//   31005 GeneralErrors.ConnectionError
//   31009 GeneralErrors.TransportError   ("no transport available")
//   53000 SignalingErrors.ConnectionError
//   53001 SignalingErrors.ConnectionDisconnected
//
// Token/authorization errors (20101/20104/20151, 31204/31205) are in the set
// too. They used to be excluded on the grounds that tokenWillExpire plus the
// TOKEN_REFRESH_MS timer already covered expiry — but the SDK's tokenWillExpire
// lead time defaults to 10 s (Device's `tokenRefreshMs`), and both that and our
// own timer are setTimeouts that do not advance while the laptop is asleep. An
// expiry that slips past them had no recovery path at all. A heal is a full
// rebuild with a freshly fetched token, and the backoff ladder caps the token
// endpoint at 8 requests per outage, so this cannot become a hot loop.
//
// Deliberately EXCLUDED:
//  - Call-level failures (31400/31404/31480/31486/31603 SIP, 31401/31402 mic
//    permission, 53400/53402/53405 media). Those are one call going wrong, not
//    the registration; call.on('error') already surfaces them and tearing the
//    Device down would be an overreaction.
//  - 31000 GeneralErrors.UnknownError. Twilio uses it as a catch-all, so
//    treating it as a network fault risks a rebuild loop on unrelated faults.
const SELF_HEAL_ERROR_CODES = new Set([
  31003, 31005, 31009, 53000, 53001, 20101, 20104, 20151, 31204, 31205
]);

// Some transport failures arrive with no numeric code (e.g. a raw WebSocket
// close surfaced through the SignalingErrors namespace). Fall back to the error
// name so those still heal.
const SELF_HEAL_ERROR_NAME_RE =
  /^(TransportError|ConnectionError|ConnectionDisconnected|ConnectionTimeoutError)$/;

// Device.on('error') is fired for both device-scoped and call-scoped problems;
// the SDK passes the Call as a second argument in the latter case. A call-scoped
// error must never rebuild the Device.
const isRegistrationClassError = (err, call) => {
  if (call) return false;
  if (!err) return false;
  if (typeof err.code === 'number' && SELF_HEAL_ERROR_CODES.has(err.code)) return true;
  return !err.code && SELF_HEAL_ERROR_NAME_RE.test(err.name || '');
};

// Mount this once at the app shell (e.g. inside Home). It keeps a Twilio
// Voice Device registered for as long as the agent is marked available,
// regardless of which CMS page they're on, so incoming <Client> TwiML can
// always reach the browser. Renders the IncomingCallPill globally.
export default function GlobalIncomingCallManager() {
  const dispatch = useDispatch();
  const [available, setAvailable] = useState(false);
  // 'idle' (not available) | 'registering' | 'registered' | 'error'
  const [deviceState, setDeviceState] = useState('idle');
  const [deviceError, setDeviceError] = useState(null);
  // Latched when self-heal gives up, so the agent gets a banner that stays put
  // instead of a toast that scrolls away.
  const [deviceUnrecoverable, setDeviceUnrecoverable] = useState(false);
  const [deviceIdentity, setDeviceIdentity] = useState(null);

  const [pendingByCallSid, setPendingByCallSid] = useState({});
  const [activeIncoming, setActiveIncoming] = useState(null);
  const [incomingStatus, setIncomingStatus] = useState(null);
  const [incomingMuted, setIncomingMuted] = useState(false);

  // Conference / multi-agent state.
  // - `participantsByCallUUID` mirrors the server's Redis SET via the
  //   `participant_changed` relay so we can render the End/Leave button
  //   text and disable the Join icon for users already in.
  // - `unlockedSet` tracks which call_uuids have been promoted to a
  //   conference (server emits 'unlocked' on upgrade_done).
  const [participantsByCallUUID, setParticipantsByCallUUID] = useState({});
  const [unlockedSet, setUnlockedSet] = useState(() => new Set());
  // care_uuid is a shared constant for the care chat user, not the signed-in
  // agent; identity must come from the auth token.
  const currentUserUUID = getAuthUserInfoOrDefault().uuid || '';

  const deviceRef = useRef(null);
  const activeCallRef = useRef(null);
  // Held in a ref so the Device-lifecycle useEffect doesn't re-fire (and
  // teardown the Device!) every time pendingByCallSid mutates.
  const attachIncomingCallRef = useRef(null);
  // Set further down, once reregisterDevice() is defined. Declared up here so
  // the availability + relay listeners — which are defined above that point —
  // can reach it while staying subscribe-once.
  const reregisterDeviceRef = useRef(null);
  // callSid -> timeout id, for the device-invite watchdog above.
  const inviteWatchdogsRef = useRef({});
  // SDK listeners are attached once per call, so reading `incomingStatus`
  // directly inside them would capture the value at attach time ('ringing').
  const incomingStatusRef = useRef(null);

  // Broadcast a flag used by GlobalOutboundCallManager to block outbound
  // dials while an incoming call is active. We're "active" when there is
  // an activeIncoming row AND the status is non-terminal — i.e., ringing,
  // connecting, or connected. Terminal statuses (ended/rejected/failed/
  // disconnected) release the flag so the agent can dial out again.
  useEffect(() => {
    const TERMINAL = new Set(['ended', 'rejected', 'failed', 'disconnected']);
    const isActive = !!activeIncoming && !!incomingStatus && !TERMINAL.has(incomingStatus);
    if (typeof window !== 'undefined') {
      window.__twilioIncomingActive = isActive;
      window.dispatchEvent(
        new CustomEvent('twilio-incoming-call-active', { detail: { active: isActive } })
      );
    }
  }, [activeIncoming, incomingStatus]);

  useEffect(() => {
    incomingStatusRef.current = incomingStatus;
  }, [incomingStatus]);

  // Read inside the watchdog callback, which outlives the render that armed it.
  const deviceStateRef = useRef('idle');
  useEffect(() => {
    deviceStateRef.current = deviceState;
  }, [deviceState]);

  const stopRing = useCallback((callSid) => {
    if (!callSid) return;
    MytNotification.stopIncomingCallRing(`incoming-call-${callSid}`);
  }, []);

  // The ring is started in home/index with the parent (PSTN) CallSid from the
  // relay, but the SDK exposes both the parent (via customParameters) and the
  // child (call.parameters.CallSid) legs — and either can be missing in some
  // edge cases. Stop every id we know of so we never leave a ring playing.
  const stopAllRings = useCallback(
    (sids) => {
      if (!sids) return;
      const seen = new Set();
      for (const s of sids) {
        if (!s || seen.has(s)) continue;
        seen.add(s);
        stopRing(s);
      }
    },
    [stopRing]
  );

  const broadcastOngoingRefresh = useCallback(() => {
    window.dispatchEvent(new CustomEvent('twilio-ongoing-calls-refresh'));
  }, []);

  const clearInviteWatchdog = useCallback((sids) => {
    if (!sids) return;
    sids.filter(Boolean).forEach((sid) => {
      const timer = inviteWatchdogsRef.current[sid];
      if (timer) {
        clearTimeout(timer);
        delete inviteWatchdogsRef.current[sid];
      }
    });
  }, []);

  const attachIncomingCall = useCallback(
    (call) => {
      // Don't ring the agent if they're already on an outbound call. We
      // auto-reject the SDK call so Twilio's <Dial> action URL fires with
      // a non-completed status — the backend then routes the PSTN caller
      // to voicemail just like the no-answer path. This keeps the agent
      // focused on their current call without dropping the inbound caller
      // silently.
      if (typeof window !== 'undefined' && window.__twilioOutboundActive) {
        const rejectedCallSid = call?.parameters?.CallSid || null;
        // eslint-disable-next-line no-console
        console.warn('global_incoming.auto_rejected_outbound_active', {
          callSid: rejectedCallSid,
          parentCallSid: call?.customParameters?.get?.('ParentCallSid') || null
        });
        try {
          call.reject?.();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('global_incoming.auto_reject_failed', e);
        }
        // Tell the agent what happened. Without this the inbound call is
        // rejected and routed to voicemail with no UI trace at all, so the
        // agent never learns a customer tried to reach them.
        const autoRejectMessage = "Incoming call sent to voicemail — you're on an outbound call.";
        dispatch(set_notification_variable(true, 'error', autoRejectMessage));
        // The toast above lands in *this* tab's Redux store. The flag is
        // per-tab window state, so the tab that auto-rejects is often not the
        // tab the agent is looking at — and then the only trace is invisible.
        // An OS notification surfaces regardless of which tab has focus.
        // Silently no-ops when notification permission isn't granted.
        MytNotification.showLocalNotification(
          `twilio-auto-reject-${rejectedCallSid || Date.now()}`,
          autoRejectMessage,
          undefined,
          'Missed incoming call'
        );
        return;
      }

      activeCallRef.current = call;

      // Safari never plays the element the SDK creates for the remote leg —
      // see twilio_audio.js. Detached in every terminal handler below.
      const detachRemoteAudio = attachRemoteAudio(call);

      // The SDK invite arrived, so the watchdog above has nothing to warn
      // about. Cleared for both leg SIDs since the relay could have keyed on
      // either.
      clearInviteWatchdog([
        call?.customParameters?.get?.('ParentCallSid'),
        call?.parameters?.CallSid
      ]);

      // The SDK's call.parameters.CallSid is the child (Client) leg SID,
      // but the ring was started with the parent (PSTN) SID from the relay.
      // We track BOTH so stopRing hits whichever the ring was keyed on —
      // ParentCallSid is the expected id, but it can be missing if the TwiML
      // <Parameter> didn't propagate, in which case the ring was keyed on
      // whatever call_sid the relay carried (which equals the child here).
      const parentSid = call?.customParameters?.get?.('ParentCallSid');
      const childSid =
        call?.parameters?.CallSid || call?.customParameters?.get?.('CallSid') || null;
      const ringSids = [parentSid, childSid].filter(Boolean);
      const sid = parentSid || childSid;
      const meta = ringSids.map((s) => pendingByCallSid[s]).find(Boolean) || null;

      // Conference dial-in for a joiner: when the user clicked Join on the
      // Ongoing Calls table, we stashed the target call_uuid before the REST
      // call, and the next SDK incoming is the conference dial-in for that
      // call. Joiner pills don't show the lock icon (they're not the host).
      let joinTargetUUID = null;
      if (typeof window !== 'undefined' && window.__twilioJoinTarget) {
        joinTargetUUID = window.__twilioJoinTarget;
        window.__twilioJoinTarget = null;
        // Cancel the join-watchdog timeout — the SDK incoming arrived,
        // so the "we never connected" notification shouldn't fire.
        if (window.__twilioJoinTargetTimeout) {
          clearTimeout(window.__twilioJoinTargetTimeout);
          window.__twilioJoinTargetTimeout = null;
        }
      }

      const callUUID = (meta && meta.call_uuid) || joinTargetUUID || null;
      const isJoiner = !!joinTargetUUID;

      setActiveIncoming({
        callSid: sid || null,
        callUUID,
        isJoiner,
        ringSids,
        fromNumber: (meta && meta.from_number) || call?.parameters?.From || call?.from || 'Unknown',
        studentName: meta && meta.student_name ? meta.student_name : null
      });
      setIncomingStatus(isJoiner ? 'connecting' : 'ringing');
      setIncomingMuted(false);

      // Attach all listeners BEFORE accept() — Twilio's SDK can fire the
      // 'accept' event synchronously during the call.accept() invocation;
      // listeners attached after that miss the event and the pill never
      // transitions to 'connected'.
      call.on('accept', () => {
        setIncomingStatus('connected');
        stopAllRings(ringSids);
        broadcastOngoingRefresh();
      });
      call.on('disconnect', () => {
        setIncomingStatus('ended');
        stopAllRings(ringSids);
        detachRemoteAudio();
        broadcastOngoingRefresh();
      });
      call.on('cancel', () => {
        // 'cancel' while we were still ringing means the call went away before
        // the agent could pick up (caller hung up, or Twilio's dial timed out).
        // Say so — otherwise the pill just vanishes 5s later and the agent is
        // left wondering whether they missed it or it never really rang.
        const wasRinging = incomingStatusRef.current === 'ringing';
        // eslint-disable-next-line no-console
        console.warn('global_incoming.cancelled', { ringSids, wasRinging });
        setIncomingStatus('ended');
        stopAllRings(ringSids);
        clearInviteWatchdog(ringSids);
        detachRemoteAudio();
        broadcastOngoingRefresh();
        if (wasRinging) {
          dispatch(
            set_notification_variable(true, 'error', 'Incoming call ended before it was answered.')
          );
        }
      });
      call.on('reject', () => {
        setIncomingStatus('rejected');
        stopAllRings(ringSids);
        detachRemoteAudio();
        broadcastOngoingRefresh();
      });
      call.on('error', (err) => {
        // The pill auto-closes 5s after a terminal status, so without an
        // explicit notice a mid-ring SDK/WebRTC failure looks identical to
        // "the call never came" — the agent loses their only chance to answer
        // and never finds out why.
        console.error('global_incoming.error', err);
        setIncomingStatus('failed');
        stopAllRings(ringSids);
        clearInviteWatchdog(ringSids);
        detachRemoteAudio();
        broadcastOngoingRefresh();
        const reason = err?.message || err?.code || 'unknown error';
        dispatch(
          set_notification_variable(
            true,
            'error',
            `Incoming call failed on your device (${reason}). Refresh the page if this repeats.`
          )
        );
      });
      call.on('mute', (isMuted) => setIncomingMuted(!!isMuted));

      // Auto-accept the conference dial-in: the user already opted in by
      // clicking Join, so making them click Accept on the pill again is a
      // wart. The pill still appears so they can mute / leave / end.
      //
      // The Join click's activation window is long gone by the time Twilio
      // dials us back, so audio has to be unlocked before accept(); the click
      // itself also fires `twilio-warm-audio` (see the effect below).
      if (isJoiner) {
        unlockTwilioAudio();
        try {
          call.accept?.();
        } catch (e) {
          console.error('global_incoming.join_auto_accept_failed', e);
        }
      }
    },
    [pendingByCallSid, stopAllRings, broadcastOngoingRefresh, clearInviteWatchdog, dispatch]
  );

  useEffect(() => {
    attachIncomingCallRef.current = attachIncomingCall;
  }, [attachIncomingCall]);

  // /twilio-agent's joinOngoing fires `twilio-warm-audio` from inside the Join
  // click — the only user gesture in a conference dial-in, and the only moment
  // Safari will resume the SDK's AudioContext.
  useEffect(() => {
    const handler = () => unlockTwilioAudio();
    window.addEventListener('twilio-warm-audio', handler);
    return () => window.removeEventListener('twilio-warm-audio', handler);
  }, []);

  // Fetch availability once on mount, then rely on the
  // `twilio-agent-availability-changed` window event. Local toggles dispatch
  // it directly; server-side changes arrive via a chat relay that home/index
  // translates into the same event, so every tab stays in sync without polling.
  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      try {
        const resp = await get_api(getUrl('/v2/twilio_call/availability'), true);
        if (cancelled) return;
        setAvailable(!!resp.data?.data?.available);
      } catch (err) {
        console.error('global_incoming.availability_fetch_failed', err);
      }
    };

    refresh();

    const onAvailabilityChanged = (e) => {
      const detail = e?.detail || {};
      // The relay is broadcast to EVERY online care user and carries the
      // toggling user's ops_uuid, so an unfiltered handler would react to other
      // agents coming online — re-registering (and briefly de-registering)
      // every agent's Device whenever anyone started a shift. AvailabilityToggle
      // applies the same filter for the same reason.
      // An unknown current uuid means we cannot attribute the event, so drop
      // it — reacting would rebuild this agent's Device on someone else's
      // toggle.
      if (!currentUserUUID || (detail.ops_uuid && detail.ops_uuid !== currentUserUUID)) {
        return;
      }
      const next = !!detail.available;
      setAvailable(next);

      // PROD-8664. The backend's ghost detector flips the agent unavailable
      // after 3 silent full-ring timeouts and relays source === 'ghost_detect'.
      // The stale registration is still accepting invites and swallowing calls,
      // so drop it and rebuild immediately rather than waiting for the agent to
      // read the notice and reload. Bypasses the debounce: this is rare and the
      // agent is, by definition, currently missing calls.
      if (detail.source === 'ghost_detect') {
        // eslint-disable-next-line no-console
        console.warn('twilio_device.ghost_detect_relay', { available: next });
        reregisterDeviceRef.current?.('ghost_detect', { force: true });
        const ghostMessage =
          'Your calling device stopped delivering calls and has been reset. You were marked unavailable — toggle yourself back on when ready.';
        dispatch(set_notification_variable(true, 'error', ghostMessage));
        // The toast lands in whichever tab holds the Redux store the agent is
        // not necessarily looking at; an OS notification surfaces regardless.
        MytNotification.showLocalNotification(
          `twilio-device-ghost-${Date.now()}`,
          ghostMessage,
          undefined,
          'Calling device reset'
        );
        return;
      }

      // Shift start. Toggling availability ON is the ritual agents already
      // perform, which makes it the natural place to hang recovery off: a
      // ghost registration from yesterday's tab is replaced before the first
      // call of the shift can be swallowed by it.
      if (next) {
        reregisterDeviceRef.current?.('availability_on');
      }
    };
    window.addEventListener('twilio-agent-availability-changed', onAvailabilityChanged);

    return () => {
      cancelled = true;
      window.removeEventListener('twilio-agent-availability-changed', onAvailabilityChanged);
    };
  }, [dispatch, currentUserUUID]);

  // ── Twilio Device lifecycle ──────────────────────────────────────────
  // Registered for the lifetime of the manager (i.e., as long as the user has
  // the home shell mounted), regardless of the `available` flag. `available`
  // controls inbound call ROUTING — the server-side agentsAvailableForCall
  // picks only available users for new PSTN calls. Device registration is a
  // separate concern: it just means "my browser can receive Twilio Client
  // calls when explicitly dialed". Decoupling these lets an unavailable user
  // JOIN an ongoing call (where the server REST-creates a call to
  // client:cms_<them>) without first toggling availability and accidentally
  // inviting a fresh inbound PSTN.
  //
  // These used to be closures inside a single mount-only effect. They're
  // hoisted out so the Device can also be re-created on demand — see the
  // re-registration path below (PROD-8664). The effect is still mount-only:
  // a *plain* availability toggle must not implicitly re-run registration,
  // it goes through reregisterDevice() with its guards and debounce.
  const refreshTimerRef = useRef(null);
  // True once this manager has unmounted. Replaces the old per-effect-run
  // `cancelled` flag, so async continuations spanning a teardown still bail.
  const deviceCancelledRef = useRef(false);
  // Debounce + reentrancy state for on-demand re-registration.
  const lastReregisterAtRef = useRef(0);
  const reregisterInFlightRef = useRef(false);

  // ── Self-heal state (PROD-8664) ──────────────────────────────────────
  // True while WE are the ones taking the Device down (teardown on unmount, or
  // the destroy half of a re-registration). teardownDevice() removes the SDK
  // listeners before destroy(), so a self-inflicted 'unregistered' should never
  // reach us — but if the SDK ever emits it synchronously from inside destroy(),
  // this is what stops a rebuild from triggering another rebuild forever.
  const intentionalTeardownRef = useRef(false);
  // Consecutive failed heal attempts. Reset by a successful 'registered'.
  const selfHealAttemptsRef = useRef(0);
  const selfHealTimerRef = useRef(null);
  // Latched once the attempt cap is hit, so we stop retrying and stop nagging.
  // Cleared by a successful registration or by a fresh 'online' event.
  const selfHealExhaustedRef = useRef(false);
  // Set below, once scheduleSelfHeal() exists. Needed up here because the Device
  // event handlers live inside registerDevice(), which is defined earlier.
  const scheduleSelfHealRef = useRef(null);

  const clearSelfHealTimer = useCallback(() => {
    if (selfHealTimerRef.current) {
      clearTimeout(selfHealTimerRef.current);
      selfHealTimerRef.current = null;
    }
  }, []);

  const resetSelfHeal = useCallback(() => {
    clearSelfHealTimer();
    selfHealAttemptsRef.current = 0;
    selfHealExhaustedRef.current = false;
    setDeviceUnrecoverable(false);
  }, [clearSelfHealTimer]);

  const fetchDeviceToken = useCallback(async () => {
    const resp = await get_api(getUrl('/v2/twilio_call/token'), true);
    return {
      token: resp.data?.data?.token,
      identity: resp.data?.data?.identity
    };
  }, []);

  const clearTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Self-rescheduling, so it reaches itself through a ref rather than being
  // a recursive useCallback (which cannot reference itself in its own body).
  const scheduleTokenRefreshRef = useRef(null);
  const scheduleTokenRefresh = useCallback(() => {
    clearTokenRefresh();
    refreshTimerRef.current = setTimeout(async () => {
      refreshTimerRef.current = null;
      if (deviceCancelledRef.current) return;
      if (!deviceRef.current) return;
      try {
        const { token } = await fetchDeviceToken();
        if (deviceCancelledRef.current || !token || !deviceRef.current) return;
        deviceRef.current.updateToken(token);
        // updateToken only swaps the token on the stream — it does NOT bring an
        // already-unregistered Device back. Without this check a "successful"
        // refresh leaves a dead Device looking healthy to us.
        if (deviceRef.current.state !== 'registered') {
          scheduleSelfHealRef.current?.(
            'token_refresh_not_registered',
            'twilio_device.reregister_after_token_refresh',
            {
              deviceState: deviceRef.current.state
            }
          );
        }
        // eslint-disable-next-line no-console
        console.info('[twilio] Device token refreshed');
        scheduleTokenRefreshRef.current?.();
      } catch (err) {
        console.error('global_incoming.device.token_refresh_failed', err);
        // Retry in 1 min; a re-registration will also recover.
        refreshTimerRef.current = setTimeout(() => scheduleTokenRefreshRef.current?.(), 60 * 1000);
      }
    }, TOKEN_REFRESH_MS);
  }, [clearTokenRefresh, fetchDeviceToken]);

  useEffect(() => {
    scheduleTokenRefreshRef.current = scheduleTokenRefresh;
  }, [scheduleTokenRefresh]);

  const teardownDevice = useCallback(() => {
    console.log('[twilio] Tearing down Device');

    // Suppress the self-heal path for anything the destroy below emits. Cleared
    // in registerDevice() once a replacement Device exists.
    intentionalTeardownRef.current = true;
    clearTokenRefresh();
    const device = deviceRef.current;
    deviceRef.current = null;
    if (!device) return;
    try {
      device.removeAllListeners?.();
      device.destroy();
    } catch (err) {
      console.error('global_incoming.device.destroy_failed', err);
    }
  }, [clearTokenRefresh]);

  // Creates and registers a fresh Device (fresh token fetch included).
  // Tears down any existing Device first so we never leak a registration.
  // Resolves true only when register() completed.
  const registerDevice = useCallback(async () => {
    setDeviceState('registering');
    setDeviceError(null);

    try {
      const { token, identity } = await fetchDeviceToken();
      if (deviceCancelledRef.current) return false;
      if (!token) {
        setDeviceState('error');
        setDeviceError('no token');
        return false;
      }

      // eslint-disable-next-line no-console
      console.info('[twilio] Registering Device with identity =', identity);
      setDeviceIdentity(identity || null);

      if (deviceRef.current) teardownDevice();

      const device = new Device(token);
      deviceRef.current = device;
      // A replacement Device now exists, so any further 'unregistered'/'error'
      // from this point on is genuine and should be healed.
      intentionalTeardownRef.current = false;

      // Events from a Device we've already replaced must not act on the current
      // one. teardownDevice() removes listeners and nulls deviceRef before
      // destroy(), so an in-flight event from the old Device fails this check.
      const isCurrentDevice = () => deviceRef.current === device;

      device.on('incoming', (call) => {
        attachIncomingCallRef.current?.(call);
      });
      device.on('error', (err, call) => {
        console.error('global_incoming.device.error', err);
        if (deviceCancelledRef.current) return;
        setDeviceState('error');
        setDeviceError(err?.message || String(err));

        // Network/registration faults mean the Device can't receive invites any
        // more; rebuild it. Call-level and token errors are handled elsewhere
        // (call.on('error') and tokenWillExpire respectively) and must not
        // trigger a rebuild — see SELF_HEAL_ERROR_CODES.
        if (intentionalTeardownRef.current || !isCurrentDevice()) return;
        if (!isRegistrationClassError(err, call)) return;
        scheduleSelfHealRef.current?.('device_error', 'twilio_device.reregister_on_error', {
          code: err?.code ?? null,
          name: err?.name || null,
          message: err?.message || null
        });
      });
      device.on('registered', () => {
        // eslint-disable-next-line no-console
        console.info(
          '[twilio] Device registered for incoming calls — identity =',
          device.identity || identity
        );
        if (deviceCancelledRef.current) return;
        setDeviceState('registered');
        // We're healthy again: drop any pending retry and clear the backoff so a
        // later failure gets the full ladder rather than resuming near the cap.
        resetSelfHeal();
      });
      device.on('unregistered', () => {
        // eslint-disable-next-line no-console
        console.info('[twilio] Device unregistered');
        if (deviceCancelledRef.current) return;
        // Ours (unmount / re-registration destroy) — not a fault to heal.
        if (intentionalTeardownRef.current || !isCurrentDevice()) return;
        scheduleSelfHealRef.current?.(
          'unregistered',
          'twilio_device.reregister_on_unregistered',
          {}
        );
      });
      device.on('tokenWillExpire', async () => {
        console.log('[twilio] Device token will expire soon, refreshing...');
        // Belt-and-braces. Note the SDK's lead time here is only ~10 s by default,
        // so this fires far too late to be the primary refresh — that is the timer.
        try {
          const refreshed = await fetchDeviceToken();
          if (deviceCancelledRef.current || !refreshed.token || !deviceRef.current) return;
          deviceRef.current.updateToken(refreshed.token);
          if (deviceRef.current.state !== 'registered') {
            scheduleSelfHealRef.current?.(
              'token_will_expire_not_registered',
              'twilio_device.reregister_after_token_refresh',
              { deviceState: deviceRef.current.state }
            );
          }
          // eslint-disable-next-line no-console
          console.info('[twilio] Device token refreshed via tokenWillExpire');
        } catch (err) {
          console.error('global_incoming.device.tokenWillExpire_refresh_failed', err);
        }
      });

      await device.register();
      console.log('[twilio] Device register() called');
      scheduleTokenRefresh();
      return true;
    } catch (err) {
      console.error('global_incoming.device.register_failed', err);
      if (!deviceCancelledRef.current) {
        setDeviceState('error');
        setDeviceError(err?.message || String(err));
      }
      return false;
    }
  }, [fetchDeviceToken, teardownDevice, scheduleTokenRefresh, resetSelfHeal]);

  // Reached from the self-heal timer for the case where no Device object exists
  // at all (a previous heal died in the token fetch, so reregisterDevice's
  // "nothing registered yet" guard would silently dead-end).
  const registerDeviceRef = useRef(null);
  useEffect(() => {
    registerDeviceRef.current = registerDevice;
  }, [registerDevice]);

  // True when audio is on the line (or about to be) and a teardown would drop
  // a real conversation. Covers this manager's own incoming call plus the
  // cross-manager window flags for incoming/outbound.
  const hasLiveCall = useCallback(() => {
    const call = activeCallRef.current;
    if (call) {
      // A *closed* call can sit in the ref until the pill auto-dismisses. If
      // we treated that as live, a ghost Device could protect itself from
      // being replaced — precisely the bug we're fixing. Trust the SDK's own
      // view; only fall back to "assume live" when it won't tell us.
      let status = null;
      try {
        status = call.status?.() || null;
      } catch (e) {
        status = null;
      }
      if (status !== 'closed') return true;
    }
    if (typeof window === 'undefined') return false;
    return !!window.__twilioIncomingActive || !!window.__twilioOutboundActive;
  }, []);

  // Destroy and fully re-create the Device. `reason` is carried into the logs
  // so we can tell shift-start re-registrations apart from ghost recovery.
  // `force` skips the debounce (used for server-flagged ghost detection and the
  // self-heal backoff, both of which are rare and high-value — the debounce
  // exists to absorb toggle spam, not to throttle recovery); the live-call guard
  // is never skipped. Returns true only when register() completed, so the
  // self-heal loop can tell a real recovery from a failed attempt.
  const reregisterDevice = useCallback(
    async (reason, { force = false } = {}) => {
      if (deviceCancelledRef.current) return false;
      if (!deviceRef.current) {
        // Nothing registered yet — the mount effect owns the first
        // registration and re-registering here would race it.
        return false;
      }
      if (hasLiveCall()) {
        // eslint-disable-next-line no-console
        console.warn('twilio_device.reregister_skipped_active_call', { reason });
        return false;
      }
      if (reregisterInFlightRef.current) return false;

      const now = Date.now();
      const sinceLastMs = now - lastReregisterAtRef.current;
      if (!force && lastReregisterAtRef.current && sinceLastMs < REREGISTER_MIN_INTERVAL_MS) {
        // eslint-disable-next-line no-console
        console.info('twilio_device.reregister_debounced', {
          reason,
          sinceLastMs,
          minIntervalMs: REREGISTER_MIN_INTERVAL_MS
        });
        return false;
      }

      reregisterInFlightRef.current = true;
      lastReregisterAtRef.current = now;
      try {
        teardownDevice();
        const registered = await registerDevice();
        // eslint-disable-next-line no-console
        console.info('twilio_device.reregistered_on_available', { reason, registered });
        return !!registered;
      } finally {
        reregisterInFlightRef.current = false;
      }
    },
    [hasLiveCall, teardownDevice, registerDevice]
  );

  useEffect(() => {
    reregisterDeviceRef.current = reregisterDevice;
  }, [reregisterDevice]);

  // One heal attempt: rebuild the existing Device, or build one from scratch if
  // a previous attempt left us with none at all.
  const attemptRegistration = useCallback(async (reason) => {
    if (deviceRef.current) {
      return reregisterDeviceRef.current?.(reason, { force: true });
    }
    return registerDeviceRef.current?.();
  }, []);

  // Availability lives server-side and is entirely independent of Device state:
  // agentsAvailableForCall picks this agent purely on the stored flag, so a dead
  // Device keeps being handed calls that can only ring out. Once recovery has
  // failed for good, take ourselves out of the rotation rather than let the
  // caller pay for it.
  const markUnavailableAfterFailure = useCallback(async () => {
    try {
      await put_api(
        getUrl('/v2/twilio_call/availability'),
        { available: false, source: 'device_failure' },
        true
      );
      window.dispatchEvent(
        new CustomEvent('twilio-agent-availability-changed', {
          detail: { available: false, ops_uuid: currentUserUUID || '' }
        })
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('twilio_device.mark_unavailable_failed', err);
    }
  }, [currentUserUUID]);

  // Arm the next self-heal attempt. `logEvent` is the structured log name the
  // caller wants attributed (reregister_on_unregistered / _on_error / ...), so
  // the reason for the rebuild stays visible in the console trail.
  const scheduleSelfHeal = useCallback(
    (reason, logEvent, meta = {}) => {
      if (deviceCancelledRef.current) return;
      // Already gave up on this session; the agent has been told to reload.
      if (selfHealExhaustedRef.current) return;
      // An attempt is already pending — the SDK often fires 'unregistered' and
      // an 'error' for the same fault, and both would otherwise arm a timer.
      if (selfHealTimerRef.current) return;

      const attempt = selfHealAttemptsRef.current + 1;
      if (attempt > SELF_HEAL_MAX_ATTEMPTS) {
        selfHealExhaustedRef.current = true;
        // eslint-disable-next-line no-console
        console.error('twilio_device.reregister_backoff_exhausted', {
          reason,
          attempts: SELF_HEAL_MAX_ATTEMPTS,
          ...meta
        });
        setDeviceState('error');
        setDeviceError(meta.code ? `registration lost (${meta.code})` : 'registration lost');
        setDeviceUnrecoverable(true);
        markUnavailableAfterFailure();
        MytNotification.showLocalNotification(
          `twilio-device-unregistered-${Date.now()}`,
          SELF_HEAL_EXHAUSTED_MESSAGE,
          undefined,
          'Calling device offline'
        );
        return;
      }

      selfHealAttemptsRef.current = attempt;
      const delayMs = Math.min(
        SELF_HEAL_BASE_DELAY_MS * 2 ** (attempt - 1),
        SELF_HEAL_MAX_DELAY_MS
      );
      // eslint-disable-next-line no-console
      console.warn(logEvent, {
        reason,
        attempt,
        maxAttempts: SELF_HEAL_MAX_ATTEMPTS,
        delayMs,
        ...meta
      });

      selfHealTimerRef.current = setTimeout(async () => {
        selfHealTimerRef.current = null;
        if (deviceCancelledRef.current) return;

        // Healed itself while we waited (the SDK reconnects on its own more
        // often than not) — nothing to do.
        if (deviceRef.current?.state === 'registered') {
          resetSelfHeal();
          return;
        }
        // Never rebuild under a live call. Re-arm at the cap instead of
        // consuming an attempt, so a long call can't burn the whole ladder.
        if (hasLiveCall()) {
          // eslint-disable-next-line no-console
          console.warn('twilio_device.reregister_skipped_active_call', { reason });
          selfHealAttemptsRef.current = attempt - 1;
          selfHealTimerRef.current = setTimeout(() => {
            // Must clear before re-entering: scheduleSelfHeal treats a non-null
            // timer ref as "an attempt is already pending" and would bail,
            // stalling the heal for the rest of the session.
            selfHealTimerRef.current = null;
            scheduleSelfHealRef.current?.(reason, logEvent, meta);
          }, SELF_HEAL_MAX_DELAY_MS);
          return;
        }

        const registered = await attemptRegistration(reason);
        if (deviceCancelledRef.current) return;
        // On success the 'registered' event resets the ladder. On failure we
        // must re-arm ourselves: the rebuild tore the old Device down with
        // intentionalTeardownRef set, so no further SDK event is coming.
        if (!registered) {
          scheduleSelfHealRef.current?.(reason, logEvent, meta);
        }
      }, delayMs);
    },
    [hasLiveCall, resetSelfHeal, attemptRegistration, markUnavailableAfterFailure]
  );

  useEffect(() => {
    scheduleSelfHealRef.current = scheduleSelfHeal;
  }, [scheduleSelfHeal]);

  const isDeviceDead = useCallback(() => {
    const device = deviceRef.current;
    if (!device) return true;
    return DEAD_DEVICE_STATES.has(device.state);
  }, []);

  // Single entry point for every "is the Device actually alive?" trigger.
  // `resetLadder` is for triggers that carry NEW information (network back, tab
  // brought forward, an inbound call landing): they un-latch an exhausted ladder
  // and retry immediately. The periodic poll passes false, so it feeds the
  // normal backoff and can never turn into an endless retry.
  const checkDeviceHealth = useCallback(
    (reason, { resetLadder = false } = {}) => {
      if (deviceCancelledRef.current) return;
      if (!isDeviceDead()) return;
      // A registration is already underway (mount, or a heal). deviceRef is
      // briefly null across its token fetch, which would otherwise read as dead
      // and start a second, competing Device.
      if (deviceStateRef.current === 'registering') return;
      if (reregisterInFlightRef.current) return;
      if (hasLiveCall()) return;
      const state = deviceRef.current?.state || 'none';
      if (!resetLadder) {
        scheduleSelfHealRef.current?.(reason, 'twilio_device.reregister_on_health_poll', {
          deviceState: state
        });
        return;
      }
      // eslint-disable-next-line no-console
      console.warn('twilio_device.reregister_on_wake', { reason, deviceState: state });
      resetSelfHeal();
      attemptRegistration(reason);
    },
    [isDeviceDead, hasLiveCall, resetSelfHeal, attemptRegistration]
  );

  const checkDeviceHealthRef = useRef(null);
  useEffect(() => {
    checkDeviceHealthRef.current = checkDeviceHealth;
  }, [checkDeviceHealth]);

  // Every environmental way a Device dies while the agent sits on an open tab
  // with availability ON. `online` and `visibilitychange`/`focus` are the cheap
  // edges; the poll is the backstop for the cases that produce no event at all
  // (laptop sleep with the Wi-Fi unchanged, and the SDK's own pagehide->destroy,
  // which removes the listeners self-heal would otherwise fire from).
  useEffect(() => {
    const onWake = (reason) => () => checkDeviceHealthRef.current?.(reason, { resetLadder: true });
    const onOnline = onWake('network_restore');
    const onFocus = onWake('window_focus');
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      checkDeviceHealthRef.current?.('tab_visible', { resetLadder: true });
    };
    const poll = setInterval(() => {
      checkDeviceHealthRef.current?.('health_poll');
    }, DEVICE_HEALTH_POLL_MS);

    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(poll);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    deviceCancelledRef.current = false;
    registerDevice();
    return () => {
      deviceCancelledRef.current = true;
      // Don't let a pending heal attempt rebuild a Device for an unmounted
      // manager — that would leak a registration no listener is attached to.
      clearSelfHealTimer();
      teardownDevice();
    };
    // Mount-only: the Device stays registered across availability toggles.
    // Re-registration is driven explicitly by reregisterDevice() so it can be
    // guarded and debounced — never as a side effect of a dependency change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track relay metadata so the pill can show name, and so we can reconcile
  // with the SDK's incoming event by CallSid.
  useEffect(() => {
    const handler = (item) => {
      if (!item || !item.data) return;
      const { type } = item.data;

      if (type === 'incoming_call') {
        const callSid = item.data.call_sid;
        if (!callSid) return;
        setPendingByCallSid((prev) => ({ ...prev, [callSid]: item.data }));
        broadcastOngoingRefresh();

        // Last chance to save THIS call: the backend rings for ~25 s and a
        // rebuild lands in 1-2 s, so a Device that is dead when the relay
        // arrives can still be back in time for the invite.
        checkDeviceHealthRef.current?.('incoming_call_relay', { resetLadder: true });

        // Arm the device-invite watchdog: the ring is now audible, so if the
        // SDK invite never lands the agent hears a call they cannot answer.
        if (!inviteWatchdogsRef.current[callSid]) {
          inviteWatchdogsRef.current[callSid] = setTimeout(() => {
            delete inviteWatchdogsRef.current[callSid];
            // Invite already arrived by another SID, or the pill is up — the
            // watchdog has nothing to report.
            if (activeCallRef.current) return;
            // eslint-disable-next-line no-console
            console.warn('global_incoming.device_invite_missing', {
              callSid,
              deviceState: deviceStateRef.current,
              waitedMs: DEVICE_INVITE_WATCHDOG_MS
            });
            dispatch(set_notification_variable(true, 'error', INVITE_MISSING_MESSAGE));
            // The relay already kicked a rebuild; re-arm in case that attempt
            // was itself swallowed, so the next call is not missed too.
            checkDeviceHealthRef.current?.('invite_watchdog', { resetLadder: true });
          }, DEVICE_INVITE_WATCHDOG_MS);
        }
        return;
      }

      // PROD-8664. Backend's ghost-registration detector fires this at the
      // affected agent after repeated full-ring timeouts: their Device is
      // accepting invites but the calls never reach the UI. Payload carries a
      // human-readable message. We surface it AND rebuild the Device — telling
      // the agent without fixing anything is what made this a recurring
      // incident in the first place.
      if (type === 'device_ghost_suspected') {
        // Defensive: the backend targets the affected agent, but if it ever
        // fans this out to the "care" group the way availability_changed is
        // fanned out, only the named agent should act on it. An unknown
        // current uuid cannot be matched against, so drop the event rather
        // than rebuilding this agent's Device on someone else's alert.
        if (!currentUserUUID || (item.data.ops_uuid && item.data.ops_uuid !== currentUserUUID)) {
          return;
        }
        const message =
          item.data.message ||
          'Your calling device may have stopped receiving calls. It has been reset — reload the tab if calls still do not appear.';
        // eslint-disable-next-line no-console
        console.warn('twilio_device.ghost_suspected_relay', {
          message,
          callSid: item.data.call_sid || null
        });
        reregisterDeviceRef.current?.('device_ghost_suspected', { force: true });
        dispatch(set_notification_variable(true, 'error', message));
        MytNotification.showLocalNotification(
          `twilio-device-ghost-${item.data.call_sid || Date.now()}`,
          message,
          undefined,
          'Calling device problem'
        );
        return;
      }

      if (type === 'incoming_call_ended') {
        const endedSid = item.data.call_sid;
        clearInviteWatchdog([endedSid]);
        broadcastOngoingRefresh();
        return;
      }

      // Conference upgrade complete. Server has placed the customer +
      // originator legs into a Conference room; the Join button on the
      // ongoing-calls row becomes available, and the originator's pill
      // flips out of locked state.
      if (type === 'unlocked') {
        const callUUID = item.data.call_uuid;
        if (!callUUID) return;
        setUnlockedSet((prev) => {
          if (prev.has(callUUID)) return prev;
          const next = new Set(prev);
          next.add(callUUID);
          return next;
        });
        broadcastOngoingRefresh();
        return;
      }

      // Live participant set update. Fired on every conference join/leave.
      if (type === 'participant_changed') {
        const callUUID = item.data.call_uuid;
        const participants = Array.isArray(item.data.participants) ? item.data.participants : [];
        if (!callUUID) return;
        setParticipantsByCallUUID((prev) => ({ ...prev, [callUUID]: participants }));
      }
    };
    CallbackHandler.shared.registerRelayItemListener(handler);
    return () => {
      CallbackHandler.shared.unregisterRelayItemListener(handler);
    };
  }, [broadcastOngoingRefresh, clearInviteWatchdog, dispatch, currentUserUUID]);

  // Never leave a watchdog running past unmount — it would fire a notice for a
  // call this manager is no longer tracking.
  useEffect(
    () => () => {
      Object.values(inviteWatchdogsRef.current).forEach((timer) => clearTimeout(timer));
      inviteWatchdogsRef.current = {};
    },
    []
  );

  const acceptIncoming = () => {
    setIncomingStatus('connecting');
    unlockTwilioAudio();
    try {
      activeCallRef.current?.accept();
    } catch (err) {
      console.error('global_incoming.accept_failed', err);
      setIncomingStatus('failed');
      // accept() threw — the SDK will not emit 'accept'/'disconnect', so the
      // ring would otherwise play until MytNotification's max-timeout.
      stopAllRings(activeIncoming?.ringSids);
    }
  };

  const rejectIncoming = () => {
    try {
      activeCallRef.current?.reject();
    } catch (err) {
      console.error('global_incoming.reject_failed', err);
    }
    stopAllRings(activeIncoming?.ringSids);
    setIncomingStatus('rejected');
  };

  const toggleIncomingMute = () => {
    const call = activeCallRef.current;
    if (!call) return;
    try {
      const next = !call.isMuted();
      call.mute(next);
      setIncomingMuted(next);
    } catch (err) {
      console.error('global_incoming.mute_failed', err);
    }
  };

  const endIncoming = () => {
    try {
      activeCallRef.current?.disconnect();
    } catch (err) {
      console.error('global_incoming.disconnect_failed', err);
    }
  };

  const unlockIncoming = useCallback(async () => {
    const callUUID = activeIncoming?.callUUID;
    if (!callUUID) {
      dispatch(
        set_notification_variable(
          true,
          'error',
          'Could not resolve this call yet — try again in a moment.'
        )
      );
      return;
    }
    try {
      await post_api(getUrl('/v2/twilio_call/unlock_call'), { call_uuid: callUUID }, true);
      // The 'unlocked' relay flips the icon for everyone; no local update needed.
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('global_incoming.unlock_failed', err);
      const reason =
        err?.response?.data?.reason || err?.message || 'Failed to move call to conference';
      dispatch(set_notification_variable(true, 'error', reason));
    }
  }, [activeIncoming, dispatch]);

  const leaveIncoming = () => {
    // Just drop our SDK leg; conference continues for the remaining legs.
    try {
      activeCallRef.current?.disconnect();
    } catch (err) {
      console.error('global_incoming.leave_failed', err);
    }
  };

  const endForEveryone = useCallback(async () => {
    const callUUID = activeIncoming?.callUUID;
    // Pre-conference (no callUUID): there's nothing for the server to
    // "end for everyone" — local hangup is correct and equivalent to End.
    if (!callUUID) {
      endIncoming();
      return;
    }
    try {
      await post_api(getUrl('/v2/twilio_call/end_ongoing'), { call_uuid: callUUID }, true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('global_incoming.end_for_everyone_failed', err);
      // DON'T fall through to a local disconnect on failure — that would
      // silently behave like Leave (only this user drops, conference
      // continues), which is the opposite of the user's intent.
      const reason =
        err?.response?.data?.reason ||
        err?.message ||
        'Failed to end call for everyone — try again, or click Leave to drop just yourself.';
      dispatch(set_notification_variable(true, 'error', reason));
      return;
    }
    // REST succeeded → server REST-completes the conference; conference-end
    // webhook will tear down our SDK leg too. Local disconnect is a fast-
    // path so the pill closes without waiting for the round-trip.
    try {
      activeCallRef.current?.disconnect();
    } catch (e) {
      /* noop */
    }
  }, [activeIncoming, dispatch]);

  // Terminal pill states auto-close after a short delay so the agent isn't
  // forced to click the X manually. Manual close (onClose) short-circuits.
  useEffect(() => {
    if (!incomingStatus) return undefined;
    if (!['ended', 'rejected', 'failed', 'disconnected'].includes(incomingStatus)) {
      return undefined;
    }
    const timer = setTimeout(() => {
      closeIncomingPill();
    }, 5000);
    return () => clearTimeout(timer);
    // closeIncomingPill is defined below and doesn't change between renders;
    // including it would recreate the timer every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingStatus]);

  const closeIncomingPill = () => {
    stopAllRings(activeIncoming?.ringSids);
    clearInviteWatchdog(activeIncoming?.ringSids);
    setActiveIncoming(null);
    setIncomingStatus(null);
    activeCallRef.current = null;
    const ringSids = activeIncoming?.ringSids;
    if (ringSids && ringSids.length > 0) {
      setPendingByCallSid((prev) => {
        const next = { ...prev };
        for (const s of ringSids) delete next[s];
        return next;
      });
    }
  };

  const badgeColor =
    deviceState === 'registered'
      ? {
          bg: 'var(--state-success-bg)',
          fg: 'var(--state-success-text)',
          dot: 'var(--state-success)'
        }
      : deviceState === 'registering'
        ? {
            bg: 'var(--state-warning-bg)',
            fg: 'var(--state-warning-text)',
            dot: 'var(--state-warning)'
          }
        : deviceState === 'error'
          ? {
              bg: 'var(--state-error-bg)',
              fg: 'var(--state-error-text)',
              dot: 'var(--state-error)'
            }
          : null; // 'idle' → hidden

  return (
    <>
      {deviceUnrecoverable && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: '10px 16px',
            background: 'var(--state-error-bg)',
            color: 'var(--state-error-text)',
            borderBottom: '1px solid var(--state-error)',
            fontSize: 13,
            fontWeight: 500
          }}
        >
          <span>{SELF_HEAL_EXHAUSTED_MESSAGE}</span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '4px 12px',
              borderRadius: 6,
              border: '1px solid var(--state-error)',
              background: 'var(--surface-card)',
              color: 'var(--state-error-text)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Reload
          </button>
        </div>
      )}

      {/* {badgeColor && (
        <div
          title={
            deviceError ? `Twilio Device error: ${deviceError}` : `Twilio Device: ${deviceState}`
          }
          style={{
            position: 'fixed',
            bottom: 12,
            right: 12,
            zIndex: 9998,
            padding: '4px 10px',
            borderRadius: 9999,
            background: badgeColor.bg,
            color: badgeColor.fg,
            fontSize: 11,
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.04)'
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: 9999,
              background: badgeColor.dot
            }}
          />
          Twilio: {deviceState}
          {deviceIdentity && deviceState === 'registered' && (
            <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}> · {deviceIdentity}</span>
          )}
        </div>
      )} */}

      {activeIncoming && incomingStatus
        ? (() => {
            const { callUUID } = activeIncoming;
            const unlocked = !!callUUID && unlockedSet.has(callUUID);
            const participants = (callUUID && participantsByCallUUID[callUUID]) || [];
            const opsCount = participants.filter(
              (p) => typeof p === 'string' && p.startsWith('cms_')
            ).length;
            // Originator = the user this pill belongs to AND who answered the
            // original call. If they joined via the table, isJoiner is true.
            const isOriginator = !activeIncoming.isJoiner;
            return (
              <IncomingCallPill
                fromNumber={activeIncoming.fromNumber}
                studentName={activeIncoming.studentName}
                status={incomingStatus}
                muted={incomingMuted}
                unlocked={unlocked}
                isOriginator={isOriginator}
                participantCount={opsCount || 1}
                onAccept={acceptIncoming}
                onReject={rejectIncoming}
                onToggleMute={toggleIncomingMute}
                onEndCall={endIncoming}
                onUnlock={unlockIncoming}
                onLeave={leaveIncoming}
                onEndForEveryone={endForEveryone}
                onClose={closeIncomingPill}
              />
            );
          })()
        : null}
    </>
  );
}
